import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';
import ImageUploadField from './ImageUploadField';

const emptyForm = { title: '', image_url: '', description: '', category: '' };

const PortfolioManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`);
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setMode('create');
    setForm(emptyForm);
    setCurrent(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setMode('edit');
    setCurrent(item);
    setForm({
      title: item.title || '',
      image_url: item.image_url || '',
      description: item.description || '',
      category: item.category || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this portfolio image?')) return;
    try {
      await axios.delete(`${API}/portfolio/${id}`, getAuthHeader());
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSubmit = async () => {
    try {
      if (mode === 'create') {
        await axios.post(`${API}/portfolio`, form, getAuthHeader());
        toast.success('Portfolio image added');
      } else {
        await axios.put(`${API}/portfolio/${current.id}`, form, getAuthHeader());
        toast.success('Portfolio image updated');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div data-testid="portfolio-content">
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <ImageIcon size={28} /> Tattoo Gallery
        </h2>
        <button
          onClick={openCreate}
          data-testid="add-portfolio-btn"
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gray-800"
        >
          <Plus size={16} /> Add Portfolio Image
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200">
          <p className="font-body text-gray-500">No portfolio images yet. Click "Add Portfolio Image" to upload your first piece.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {items.map((item) => (
            <div key={item.id} className="bg-white border border-gray-200 overflow-hidden group" data-testid={`portfolio-item-${item.id}`}>
              <div className="relative aspect-square">
                <img src={item.image_url} alt={item.title || 'Portfolio'} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button onClick={() => openEdit(item)} className="p-2 bg-white rounded-full hover:bg-gray-100" data-testid={`edit-portfolio-${item.id}`}>
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600" data-testid={`delete-portfolio-${item.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-heading font-bold">{item.title || 'Untitled'}</h3>
                {item.category && (
                  <span className="font-mono text-[10px] uppercase bg-gray-100 px-2 py-1 mt-2 inline-block">{item.category}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={`${mode === 'create' ? 'Add' : 'Edit'} Portfolio Image`}
        footer={
          <button
            onClick={handleSubmit}
            disabled={!form.image_url}
            data-testid="portfolio-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:opacity-50"
          >
            {mode === 'create' ? 'Add Portfolio Image' : 'Save Changes'}
          </button>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="portfolio-form-title"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Image *"
            testId="portfolio-image"
          />
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Category</label>
            <input
              type="text"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g., Realism, Geometric, Botanical"
              data-testid="portfolio-form-category"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="portfolio-form-description"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PortfolioManager;
